import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useCommunityThread } from '../hooks/useCommunityThread'
import { OpportunityCommunityEntry } from '../components/OpportunityCommunityEntry'

export type CommunityTarget = {
  opportunityId: number
  symbol: string
  name?: string | null
  logoUrl?: string | null
}

type CommunityDrawerContextValue = {
  open: boolean
  target: CommunityTarget | null
  focusMessageId: number | null
  openCommunityDrawer: (
    target: CommunityTarget,
    options?: { focusMessageId?: number | string | null },
  ) => void
  closeCommunityDrawer: () => void
}

const CommunityDrawerContext =
  React.createContext<CommunityDrawerContextValue | undefined>(undefined)

export function useCommunityDrawer() {
  const ctx = React.useContext(CommunityDrawerContext)
  if (!ctx) {
    throw new Error('useCommunityDrawer must be used within CommunityDrawerProvider')
  }
  return ctx
}

export function CommunityDrawerProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)
  const [target, setTarget] = React.useState<CommunityTarget | null>(null)
  const [focusMessageId, setFocusMessageId] = React.useState<number | null>(null)

  const { user, profile } = useAuth()
  const currentUserName = profile?.full_name || user?.email || 'Usuário'
  const currentUserAvatar = (profile as any)?.avatar_url ?? null
  const currentUserId = user?.id ?? null

  const threadHook = useCommunityThread(
    target ? target.opportunityId : null,
    target
      ? {
          name: currentUserName,
          avatarUrl: currentUserAvatar,
        }
      : null,
    open && !!target,
  )

  const openCommunityDrawer = React.useCallback((next: CommunityTarget, options?: { focusMessageId?: number | string | null }) => {
    setTarget(next)
    const raw = options?.focusMessageId
    const n = raw == null ? Number.NaN : Number(String(raw).trim())
    setFocusMessageId(Number.isFinite(n) && n > 0 ? Math.floor(n) : null)
    setOpen(true)
  }, [])

  const closeCommunityDrawer = React.useCallback(() => {
    setOpen(false)
    setFocusMessageId(null)
  }, [])

  return (
    <CommunityDrawerContext.Provider
      value={{ open, target, focusMessageId, openCommunityDrawer, closeCommunityDrawer }}
    >
      {children}
      {target && (
        <OpportunityCommunityEntry
          opportunityId={target.opportunityId}
          symbol={target.symbol}
          name={target.name}
          logoUrl={target.logoUrl}
          enabled
          summary={null}
          forceOpen={open}
          onOpenChange={setOpen}
          threadHook={threadHook}
          focusMessageId={focusMessageId}
        />
      )}
    </CommunityDrawerContext.Provider>
  )
}
